/**
 * An always-mounted polite live region.
 *
 * **Always mounted, empty when there is nothing to say.** A `role="status"` inserted together with
 * its text is a region that did not exist when the change happened, and screen readers do not
 * consistently announce one — the region has to be in the accessibility tree BEFORE the content
 * arrives. That is the same rule the builder's overflow banner and save-error line already follow,
 * and the reason both of them render unconditionally with an empty string.
 *
 * `status` rather than `alert`: these are the trainer's own acts succeeding, not something
 * interrupting them. A refused drop is announced here too — it is a finding about where they aimed,
 * not an error to cut across what they are reading.
 *
 * ── `aria-live` is stated, not left to the role ─────────────────────────────────────────────
 * `role="status"` implies a polite live region, but Base UI's modal dialogs hide everything outside
 * themselves with `aria-hidden` and exempt only elements carrying an explicit `[aria-live]`
 * (floating-ui's `markOthers`). A bare `role="status"` behind an open card was therefore under an
 * `aria-hidden` ancestor, and every notice spoken while the card was up reached nobody. `aria-atomic`
 * because each message is one sentence that replaces the last, and a reader that spoke only the
 * changed words would say „zapisane" without what was saved.
 *
 * Visually hidden by the shared clip rule rather than by `display: none`, which would take the
 * region back out of the accessibility tree and undo the whole point.
 */

import styles from './LiveRegionAnnouncer.module.css';

interface LiveRegionAnnouncerProps {
  readonly message: string;
  /** An extra class from the host, when the region has to sit somewhere specific in a layout. */
  readonly className?: string;
}

export function LiveRegionAnnouncer({ message, className }: LiveRegionAnnouncerProps) {
  return (
    <p className={`${String(styles.region)} ${className ?? ''}`} role="status" aria-live="polite" aria-atomic="true">
      {message}
    </p>
  );
}
